import type {
  MedicationActivity,
  DeepPartial,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
} from "@tandemdiabetes/sugarmate-react-components"
import { useCallback } from "react"

import { usePolyglot } from "src/contexts"
import { useSliderInputProps } from "src/hooks"
import styles from "src/styles/entryModal.module.scss"

interface Props {
  units?: string;
  activity?: DeepPartial<MedicationActivity>;
  updateActivity: (a: DeepPartial<MedicationActivity>) => void;
  readOnly?: boolean;
}

const id = "medication-units-range-input"
const max = 10
const min = 0
const step = 1

/*
 * renders a number label and range input for choosing number of units
 */
export function MedicationFormUnitsRangeInput(props: Props) {
  const {
    units,
    activity,
    updateActivity,
    readOnly = false,
  } = props

  const polyglot = usePolyglot()

  const onChange = useCallback(
    (numUnits: number) => {
      updateActivity({
        num_units: numUnits,
      })
    },
    [updateActivity],
  )

  const value = typeof activity?.num_units === "number"
    ? activity.num_units
    : null

  const {
    inputValue,
    sliderValue,
    onNumberChange,
    onSliderChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    min,
    max,
    inputStep: step,
    sliderStep: step,
    value,
    onChange,
    allowEmptyValue: true,
  })

  return (
    <div className={styles.row}>
      <SliderValue sizePreset="large">
        <SliderValueInput
          max={max}
          min={min}
          step={step}
          id={id}
          value={inputValue}
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          onKeyDown={onNumberKeyDown}
          readOnly={readOnly}
        />
        <SliderValueLabelContainer>
          <SliderValueLabel htmlFor={id}>
            {units ||
              polyglot.t(
                "components.entry_modal.medication.default_label_detail",
                activity?.num_units ?? 0,
              )}
          </SliderValueLabel>
        </SliderValueLabelContainer>
      </SliderValue>
      {!readOnly && (
        <Slider className={styles.rangeInputSlider}>
          <SliderInput
            min={min}
            max={max}
            step={step}
            id={id}
            value={sliderValue ?? min}
            onChange={onSliderChange}
          />
          <SliderTracks
            min={min}
            max={max}
          />
        </Slider>
      )}
    </div>
  )
}
